import React, { useEffect, useState } from "react";
import { Plus, Edit3, Trash2, Layers } from "lucide-react";
import { toast } from "sonner";
import ServicePrice from "../../components/ServicePrice";
import AdminLayout from "../AdminLayout";
import { adminAxios, formatErr } from "../api";

function CatalogRow({ item, onDelete, onEdit, testid }) {
  return (
    <tr data-testid={testid} className="border-t border-slate-100 hover:bg-slate-50">
      <td className="px-5 py-3 font-medium text-slate-900">{item.id}</td>
      <td className="px-5 py-3 text-slate-700">{item.name}</td>
      <td className="px-5 py-3 text-slate-700">{item.display || item.name}</td>
      <td className="px-5 py-3 text-slate-700 capitalize">{item.category || "—"}</td>
      <td className="px-5 py-3"><ServicePrice service={item} /></td>
      <td className="px-5 py-3 text-right">
        <div className="inline-flex gap-2">
          {onEdit && (
            <button
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 text-slate-700 hover:bg-slate-100"
              onClick={() => onEdit(item)}
              data-testid={`${testid}-edit`}
            >
              <Edit3 className="w-4 h-4" /> Edit
            </button>
          )}
          <button
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 text-slate-700 hover:bg-slate-100"
            onClick={() => onDelete(item)}
            data-testid={`${testid}-delete`}
          >
            <Trash2 className="w-4 h-4" /> Delete
          </button>
        </div>
      </td>
    </tr>
  );
}

export default function AdminCatalog() {
  const [categories, setCategories] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [categoryForm, setCategoryForm] = useState({ id: "", name: "", display: "", short: "", tagline: "", original_price: 0, discounted_price: "" });
  const [serviceForm, setServiceForm] = useState({ id: "", category: "", name: "", original_price: 0, discounted_price: "", time: "", desc: "", includes: [] });
  const [editingCategoryId, setEditingCategoryId] = useState("");
  const [editingServiceId, setEditingServiceId] = useState("");

  const loadCatalog = async () => {
    setLoading(true);
    try {
      const { data } = await adminAxios.get("/catalog");
      setCategories(data.categories || []);
      setServices((data.categories || []).flatMap((cat) => cat.services || []));
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadCatalog(); }, []);

  const createCategory = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const original = Number(categoryForm.original_price);
      const discounted = categoryForm.discounted_price === "" ? null : Number(categoryForm.discounted_price);
      if (categoryForm.original_price === "" || !Number.isInteger(original) || original < 0) {
        toast.error("Original category price is required and must be a nonnegative whole-rupee amount.");
        return;
      }
      if (discounted !== null && (!Number.isInteger(discounted) || discounted < 0 || discounted > original)) {
        toast.error(`Discounted category price must be between ₹0 and ₹${original}, or left blank.`);
        return;
      }
      const payload = { ...categoryForm, original_price: original, discounted_price: discounted };
      if (editingCategoryId) {
        const patch = { name: payload.name, display: payload.display, short: payload.short, tagline: payload.tagline, original_price: payload.original_price, discounted_price: payload.discounted_price };
        await adminAxios.patch(`/catalog/categories/${editingCategoryId}`, patch);
        toast.success("Category updated");
        setEditingCategoryId("");
      } else {
        await adminAxios.post("/catalog/categories", payload);
        toast.success("Category added");
      }
      setCategoryForm({ id: "", name: "", display: "", short: "", tagline: "", original_price: 0, discounted_price: "" });
      await loadCatalog();
      window.dispatchEvent(new Event("catalog-updated"));
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setSaving(false);
    }
  };

  const createService = async (e) => {
    e.preventDefault();
    if (!serviceForm.category) {
      toast.error("Please choose a category");
      return;
    }
    setSaving(true);
    try {
      const original = Number(serviceForm.original_price);
      const discounted = serviceForm.discounted_price === "" ? null : Number(serviceForm.discounted_price);
      if (serviceForm.original_price === "" || !Number.isInteger(original) || original < 0) {
        toast.error("Original price is required. Enter a whole-rupee amount of zero or more.");
        return;
      }
      if (discounted !== null && (!Number.isInteger(discounted) || discounted < 0)) {
        toast.error("Discounted price must be a whole-rupee amount of zero or more, or left blank.");
        return;
      }
      if (discounted !== null && discounted > original) {
        toast.error(`Discounted price (₹${discounted}) cannot exceed original price (₹${original}). Enter the final charge after discount.`);
        return;
      }
      const payload = { ...serviceForm, original_price: original, discounted_price: discounted, includes: serviceForm.includes.filter(Boolean) };
      if (editingServiceId) {
        const patch = { category: payload.category, name: payload.name, original_price: payload.original_price, discounted_price: payload.discounted_price, time: payload.time, desc: payload.desc, includes: payload.includes };
        await adminAxios.patch(`/catalog/services/${editingServiceId}`, patch);
        toast.success("Service updated");
        setEditingServiceId("");
      } else {
        await adminAxios.post("/catalog/services", payload);
        toast.success("Service added");
      }
      setServiceForm({ id: "", category: "", name: "", original_price: 0, discounted_price: "", time: "", desc: "", includes: [] });
      await loadCatalog();
      window.dispatchEvent(new Event("catalog-updated"));
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setSaving(false);
    }
  };

  const removeCategory = async (cat) => {
    if (!window.confirm(`Delete category ${cat.name}? This also removes its services.`)) return;
    setSaving(true);
    try {
      await adminAxios.delete(`/catalog/categories/${cat.id}`);
      toast.success("Category deleted");
      await loadCatalog();
      window.dispatchEvent(new Event("catalog-updated"));
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setSaving(false);
    }
  };

  const removeService = async (svc) => {
    if (!window.confirm(`Delete service ${svc.name}?`)) return;
    setSaving(true);
    try {
      await adminAxios.delete(`/catalog/services/${svc.id}`);
      toast.success("Service deleted");
      await loadCatalog();
      window.dispatchEvent(new Event("catalog-updated"));
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setSaving(false);
    }
  };

  const startEditingService = (s) => {
    setServiceForm({ id: s.id, category: s.category || "", name: s.name || "", original_price: s.original_price ?? s.price ?? 0, discounted_price: s.discounted_price ?? "", time: s.time || "", desc: s.desc || "", includes: s.includes || [] });
    setEditingServiceId(s.id);
  };

  return (
    <AdminLayout title="Catalog Management">
      <div className="grid grid-cols-1 xl:grid-cols-[1.2fr,0.8fr] gap-6">
        <section className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center gap-3 mb-5">
            <Layers className="w-5 h-5 text-[#1A3C6E]" />
            <div>
              <h2 className="font-display font-bold text-lg text-slate-900">Catalog overview</h2>
              <p className="text-sm text-slate-500">Manage categories and services shown on the public site.</p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
            <div className="bg-slate-50 rounded-2xl p-4">
              <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Categories</div>
              <div className="text-3xl font-display font-extrabold text-slate-900">{categories.length}</div>
            </div>
            <div className="bg-slate-50 rounded-2xl p-4">
              <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Services</div>
              <div className="text-3xl font-display font-extrabold text-slate-900">{services.length}</div>
            </div>
          </div>

          <div className="space-y-8">
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold uppercase tracking-widest text-slate-500">Categories</h3>
              </div>
              <div className="overflow-x-auto border border-slate-200 rounded-2xl">
                <table className="w-full text-sm text-left">
                  <thead className="bg-slate-50 text-slate-500 uppercase tracking-wider text-xs">
                    <tr>
                      <th className="px-5 py-3">ID</th>
                      <th className="px-5 py-3">Name</th>
                      <th className="px-5 py-3">Display</th>
                      <th className="px-5 py-3">Starting</th>
                      <th className="px-5 py-3 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr><td colSpan={5} className="px-5 py-6 text-center text-slate-400">Loading…</td></tr>
                    ) : categories.length === 0 ? (
                      <tr><td colSpan={5} className="px-5 py-6 text-center text-slate-400">No categories found.</td></tr>
                    ) : categories.map((cat) => (
                      <tr key={cat.id} className="border-t border-slate-100 hover:bg-slate-50">
                        <td className="px-5 py-3 font-mono text-xs text-slate-700">{cat.id}</td>
                        <td className="px-5 py-3 font-medium text-slate-900">{cat.name}</td>
                        <td className="px-5 py-3 text-slate-700">{cat.display || "—"}</td>
                        <td className="px-5 py-3 text-slate-700"><ServicePrice service={{ ...cat, price: cat.startingPrice }} /></td>
                        <td className="px-5 py-3 text-right">
                          <div className="inline-flex gap-2">
                            <button
                              onClick={() => {
                                setCategoryForm({ id: cat.id, name: cat.name, display: cat.display || "", short: cat.short || "", tagline: cat.tagline || "", original_price: cat.original_price ?? cat.startingPrice ?? 0, discounted_price: cat.discounted_price ?? "" });
                                setEditingCategoryId(cat.id);
                              }}
                              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 text-slate-700 hover:bg-slate-100"
                              data-testid={`cat-edit-${cat.id}`}
                            >
                              <Edit3 className="w-4 h-4" /> Edit
                            </button>
                            <button
                              onClick={() => removeCategory(cat)}
                              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 text-slate-700 hover:bg-slate-100"
                              data-testid={`cat-delete-${cat.id}`}
                            >
                              <Trash2 className="w-4 h-4" /> Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold uppercase tracking-widest text-slate-500">Services</h3>
              </div>
              <div className="overflow-x-auto border border-slate-200 rounded-2xl">
                <table className="w-full text-sm text-left">
                  <thead className="bg-slate-50 text-slate-500 uppercase tracking-wider text-xs">
                    <tr>
                      <th className="px-5 py-3">ID</th>
                      <th className="px-5 py-3">Name</th>
                      <th className="px-5 py-3">Category</th>
                      <th className="px-5 py-3">Price</th>
                      <th className="px-5 py-3 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr><td colSpan={5} className="px-5 py-6 text-center text-slate-400">Loading…</td></tr>
                    ) : services.length === 0 ? (
                      <tr><td colSpan={5} className="px-5 py-6 text-center text-slate-400">No services found.</td></tr>
                    ) : services.map((svc) => (
                      <CatalogRow
                        key={svc.id}
                        item={svc}
                        onDelete={removeService}
                        onEdit={startEditingService}
                        testid={"svc-row-" + svc.id}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>

        <aside className="space-y-6">
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center gap-3 mb-4">
              <Plus className="w-4 h-4 text-[#1A3C6E]" />
              <h3 className="font-display font-bold text-sm text-slate-900">Add category</h3>
            </div>
            <form onSubmit={createCategory} className="space-y-4" data-testid="cat-form">
              <Input label="Category ID" value={categoryForm.id} onChange={(v) => setCategoryForm((s) => ({ ...s, id: v }))} disabled={!!editingCategoryId} />
              <Input label="Name" value={categoryForm.name} onChange={(v) => setCategoryForm((s) => ({ ...s, name: v }))} />
              <Input label="Display name" value={categoryForm.display} onChange={(v) => setCategoryForm((s) => ({ ...s, display: v }))} />
              <Input label="Short label" value={categoryForm.short} onChange={(v) => setCategoryForm((s) => ({ ...s, short: v }))} />
              <Input label="Tagline" value={categoryForm.tagline} onChange={(v) => setCategoryForm((s) => ({ ...s, tagline: v }))} />
              <Input label="Original starting price (₹)" value={categoryForm.original_price} type="number" onChange={(v) => setCategoryForm((s) => ({ ...s, original_price: v }))} />
              <Input label="Discounted starting price (₹, optional)" value={categoryForm.discounted_price} type="number" onChange={(v) => setCategoryForm((s) => ({ ...s, discounted_price: v }))} />
              <p className="text-xs text-slate-500">Category prices are advertised starting rates. Service prices determine booking charges. Leave the discount blank for no discount.</p>
              <button
                type="submit"
                disabled={saving}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[#1A3C6E] text-white hover:bg-[#132D53] disabled:opacity-60"
                data-testid="cat-submit"
              >
                <SaveIcon className="w-4 h-4" /> {saving ? "Saving…" : editingCategoryId ? "Update category" : "Save category"}
              </button>
              {editingCategoryId && (
                <button
                  type="button"
                  onClick={() => { setCategoryForm({ id: "", name: "", display: "", short: "", tagline: "", original_price: 0, discounted_price: "" }); setEditingCategoryId(""); }}
                  className="w-full mt-2 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"
                >
                  Cancel edit
                </button>
              )}
            </form>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center gap-3 mb-4">
              <Layers className="w-4 h-4 text-[#1A3C6E]" />
              <h3 className="font-display font-bold text-sm text-slate-900">Add service</h3>
            </div>
            <form onSubmit={createService} className="space-y-4" data-testid="svc-form">
              <Select
                label="Category"
                value={serviceForm.category}
                options={[{ value: "", label: "Select category" }, ...categories.map((cat) => ({ value: cat.id, label: cat.name }))]}
                onChange={(v) => setServiceForm((s) => ({ ...s, category: v }))}
              />
              <Input label="Service ID" value={serviceForm.id} onChange={(v) => setServiceForm((s) => ({ ...s, id: v }))} disabled={!!editingServiceId} />
              <Input label="Name" value={serviceForm.name} onChange={(v) => setServiceForm((s) => ({ ...s, name: v }))} />
              <Input label="Original price (₹)" type="number" value={serviceForm.original_price} onChange={(v) => setServiceForm((s) => ({ ...s, original_price: v }))} />
              <Input label="Discounted price (₹, optional)" type="number" value={serviceForm.discounted_price} onChange={(v) => setServiceForm((s) => ({ ...s, discounted_price: v }))} />
              <p className="text-xs text-slate-500">Enter the final charge after discount, e.g. original ₹499 and discounted ₹399. Leave it blank for no discount.</p>
              <Input label="Time" value={serviceForm.time} onChange={(v) => setServiceForm((s) => ({ ...s, time: v }))} />
              <Input label="Description" value={serviceForm.desc} onChange={(v) => setServiceForm((s) => ({ ...s, desc: v }))} />
              <Input label="Includes (comma separated)" value={serviceForm.includes.join(", ")} onChange={(v) => setServiceForm((s) => ({ ...s, includes: v.split(",").map((x) => x.trim()).filter(Boolean) }))} />
              <button
                type="submit"
                disabled={saving}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[#FF6B2B] text-white hover:bg-[#E65A20] disabled:opacity-60"
                data-testid="svc-submit"
              >
                <SaveIcon className="w-4 h-4" /> {saving ? "Saving…" : editingServiceId ? "Update service" : "Save service"}
              </button>
              {editingServiceId && (
                <button
                  type="button"
                  onClick={() => { setServiceForm({ id: "", category: "", name: "", original_price: 0, discounted_price: "", time: "", desc: "", includes: [] }); setEditingServiceId(""); }}
                  className="w-full mt-2 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"
                >
                  Cancel edit
                </button>
              )}
            </form>
          </div>
        </aside>
      </div>
    </AdminLayout>
  );
}

const SaveIcon = ({ className }) => <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2Z"/><path d="M17 21v-8H7v8m0-16V7h10M7 3h6"/></svg>;

const Input = ({ label, value, onChange, type = "text", disabled = false }) => (
  <div>
    <label className="block text-xs font-semibold text-slate-500 mb-2">{label}</label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 focus:border-[#1A3C6E] focus:ring-2 focus:ring-[#1A3C6E]/10 outline-none"
    />
  </div>
);

const Select = ({ label, value, options, onChange }) => (
  <div>
    <label className="block text-xs font-semibold text-slate-500 mb-2">{label}</label>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 focus:border-[#1A3C6E] focus:ring-2 focus:ring-[#1A3C6E]/10 outline-none"
    >
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
  </div>
);
